import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import {
  calcPerformanceInsights,
  calcRiskManagementInsights,
  calcExecutionInsights,
  calcPositionSizeDisciplineInsights,
  calcMistakeDetectionInsights,
  applyWidgetFilters,
  type WidgetDateRange,
} from './lib/tradeCalculations'
import { useDashboardCurrency, convertPnl } from './hooks/useDashboardCurrency'

interface Trade {
  id: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  symbol: string
  exchange?: string | null
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  pnl_percentage: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  time_interval: string | null
  is_public?: boolean | null
  current_price?: number | null
  current_price_updated_at?: string | null
  previous_close_price?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  daily_change_percentage?: number | null
  created_at: string
  updated_at: string
}

interface Strategy {
  id: string
  display_name: string
}

interface AiTradeInsight {
  summary: string
  trade_count: number
  insight_year: number
  generated_at: string
}

const RANGE_OPTIONS: { labelKey: string; value: WidgetDateRange }[] = [
  { labelKey: 'insights:timeRange.all', value: 'all' },
  { labelKey: 'insights:timeRange.1d', value: '1d' },
  { labelKey: 'insights:timeRange.1w', value: '1w' },
  { labelKey: 'insights:timeRange.1m', value: '1m' },
  { labelKey: 'insights:timeRange.3m', value: '3m' },
  { labelKey: 'insights:timeRange.6m', value: '6m' },
  { labelKey: 'insights:timeRange.ytd', value: 'ytd' },
  { labelKey: 'insights:timeRange.1y', value: '1y' },
]

const DATA_QUALITY_THRESHOLD = 70
const MIN_TRADES_FOR_PERFORMANCE_TABLE = 5

function Insights({
  onBack,
  setCurrentPage,
  embedded,
}: {
  onBack: () => void
  setCurrentPage: (page: 'home' | 'all-trades' | 'analytics' | 'insights' | 'tools' | 'strategies' | 'social-trade' | 'account' | 'community') => void
  embedded?: boolean
}) {
  const { t } = useTranslation(['trading', 'common', 'dashboard', 'insights'])
  const { user, userProfile, membershipType } = useAuth()
  const {
    selectedCurrency,
    exchangeRates,
    fetchExchangeRates,
    fetchExchangeRatesForEntryDates,
  } = useDashboardCurrency(userProfile?.preferred_currency)
  const [trades, setTrades] = useState<Trade[]>([])
  const [positionSizeRates, setPositionSizeRates] = useState<Map<string, number>>(new Map())
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [loading, setLoading] = useState(true)
  const [aiInsight, setAiInsight] = useState<AiTradeInsight | null>(null)
  const [aiInsightLoading, setAiInsightLoading] = useState(true)
  const [aiInsightGenerating, setAiInsightGenerating] = useState(false)
  const [aiGenerationStatus, setAiGenerationStatus] = useState<'success' | 'no-data' | 'error' | null>(null)
  const [selectedRange, setSelectedRange] = useState<WidgetDateRange>('ytd')
  const isAdmin = userProfile?.is_admin === true
  const canUseAiInsights =
    membershipType === 'pro' || membershipType === 'publisher' || isAdmin

  const fetchAiInsight = useCallback(async () => {
    if (!user || !canUseAiInsights) {
      setAiInsight(null)
      setAiInsightLoading(false)
      return
    }

    setAiInsightLoading(true)
    try {
      const { data, error } = await supabase
        .from('ai_trade_insights')
        .select('summary, trade_count, insight_year, generated_at')
        .eq('user_id', user.id)
        .eq('insight_year', new Date().getFullYear())
        .maybeSingle()

      if (error) throw error
      setAiInsight((data as AiTradeInsight | null) ?? null)
    } catch (err) {
      console.error('Failed to load AI trade insight:', err)
      setAiInsight(null)
    } finally {
      setAiInsightLoading(false)
    }
  }, [user?.id, canUseAiInsights])

  useEffect(() => {
    fetchAiInsight()
  }, [fetchAiInsight])

  const generateAiInsight = async () => {
    if (!user || !isAdmin || aiInsightGenerating) return

    setAiInsightGenerating(true)
    setAiGenerationStatus(null)
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-insights', {
        body: {
          user_id: user.id,
          year: new Date().getFullYear(),
        },
      })
      if (error) throw error
      if (data?.skipped) {
        setAiGenerationStatus('no-data')
        return
      }

      await fetchAiInsight()
      setAiGenerationStatus('success')
    } catch (err) {
      console.error('Failed to generate AI trade insight:', err)
      setAiGenerationStatus('error')
    } finally {
      setAiInsightGenerating(false)
    }
  }

  useEffect(() => {
    const fetchTrades = async () => {
      if (!user) {
        setTrades([])
        setLoading(false)
        return
      }
      try {
        const { data, error } = await supabase
          .from('trades_with_market_data')
          .select('*')
          .eq('user_id', user.id)
          .order('entry_date', { ascending: false })
        if (error) throw error
        const baseTrades = (data || []) as Trade[]
        const uniqueSymbols = Array.from(new Set(baseTrades.map((t) => t.symbol).filter(Boolean)))
        const uniqueMarkets = Array.from(new Set(baseTrades.map((t) => t.market).filter(Boolean)))
        if (uniqueSymbols.length > 0) {
          let symbolsQuery = supabase
            .from('symbols')
            .select('symbol, market, exchange')
            .in('symbol', uniqueSymbols)
          if (uniqueMarkets.length > 0) {
            symbolsQuery = symbolsQuery.in('market', uniqueMarkets)
          }
          const { data: symbolsData, error: symbolsError } = await symbolsQuery
          if (!symbolsError && symbolsData) {
            const exchangeByKey = new Map<string, string | null>()
            for (const s of symbolsData as Array<{ symbol: string; market: string; exchange: string | null }>) {
              const key = `${s.market}::${s.symbol}`.toUpperCase()
              if (!exchangeByKey.has(key) || (exchangeByKey.get(key) == null && s.exchange != null)) {
                exchangeByKey.set(key, s.exchange)
              }
            }
            const hydrated = baseTrades.map((t) => {
              const key = `${t.market}::${t.symbol}`.toUpperCase()
              return { ...t, exchange: exchangeByKey.get(key) ?? t.exchange ?? null }
            })
            setTrades(hydrated)
          } else {
            setTrades(baseTrades)
          }
        } else {
          setTrades(baseTrades)
        }
      } catch (err) {
        console.error('Failed to load trades:', err)
        setTrades([])
      } finally {
        setLoading(false)
      }
    }
    fetchTrades()
  }, [user?.id])

  useEffect(() => {
    const fetchStrategies = async () => {
      if (!user) return
      try {
        const { data, error } = await supabase
          .from('strategies')
          .select('id, display_name')
          .or(`user_id.eq.${user.id},is_public.eq.true`)
          .eq('is_active', true)
          .order('display_name', { ascending: true })
        if (error) throw error
        setStrategies(data || [])
      } catch (err) {
        console.error('Failed to load strategies:', err)
      }
    }
    fetchStrategies()
  }, [user?.id])

  const strategyNames = useMemo(() => {
    const map: Record<string, string> = {}
    for (const s of strategies) {
      map[s.id] = s.display_name
    }
    return map
  }, [strategies])

  const closedTradesForPerformance = useMemo(() => {
    const filtered = applyWidgetFilters(trades, {
      dateRange: selectedRange,
      strategyId: '',
      exchange: 'all',
      market: 'all',
    })
    return filtered.filter(
      (t) =>
        t.exit_date &&
        ((t as { pnl?: number | null }).pnl != null ||
          (t as { convertedPnl?: number | null }).convertedPnl != null)
    )
  }, [trades, selectedRange])

  useEffect(() => {
    if (closedTradesForPerformance.length === 0) return
    fetchExchangeRates(selectedCurrency, closedTradesForPerformance)
  }, [closedTradesForPerformance, selectedCurrency, fetchExchangeRates])

  const tradesWithConvertedPnl = useMemo(() => {
    return trades.map((t) => ({
      ...t,
      convertedPnl:
        t.exit_date && t.pnl != null
          ? convertPnl(t, selectedCurrency, exchangeRates)
          : undefined,
    }))
  }, [trades, selectedCurrency, exchangeRates])

  const performanceInsights = useMemo(() => {
    return calcPerformanceInsights(tradesWithConvertedPnl, selectedRange, strategyNames)
  }, [tradesWithConvertedPnl, selectedRange, strategyNames])

  const riskManagementInsights = useMemo(() => {
    return calcRiskManagementInsights(trades, selectedRange)
  }, [trades, selectedRange])

  const executionInsights = useMemo(() => {
    return calcExecutionInsights(trades, selectedRange)
  }, [trades, selectedRange])

  const mistakeDetectionInsights = useMemo(() => {
    return calcMistakeDetectionInsights(trades, selectedRange)
  }, [trades, selectedRange])

  const closedTradesForPositionSize = useMemo(() => {
    const filtered = applyWidgetFilters(trades, {
      dateRange: selectedRange,
      strategyId: '',
      exchange: 'all',
      market: 'all',
    })
    return filtered.filter(
      (t) =>
        t.exit_date &&
        (t as { pnl?: number | null }).pnl != null &&
        t.total_entry_amount != null &&
        t.total_entry_amount > 0 &&
        t.entry_date
    )
  }, [trades, selectedRange])

  useEffect(() => {
    if (closedTradesForPositionSize.length === 0) {
      setPositionSizeRates(new Map())
      return
    }
    fetchExchangeRatesForEntryDates(selectedCurrency, closedTradesForPositionSize).then(
      setPositionSizeRates
    )
  }, [closedTradesForPositionSize, selectedCurrency, fetchExchangeRatesForEntryDates])

  const convertPositionToDisplay = useCallback(
    (
      amount: number,
      entryDate: string,
      currency: string | null
    ): number | null => {
      if (!currency || currency === selectedCurrency) return amount
      const date = entryDate.split('T')[0]
      const rate = positionSizeRates.get(`${date}:${selectedCurrency}:${currency}`)
      if (rate != null) return amount / rate
      const reverseRate = positionSizeRates.get(`${date}:${currency}:${selectedCurrency}`)
      if (reverseRate != null) return amount * reverseRate
      return null
    },
    [selectedCurrency, positionSizeRates]
  )

  const positionSizeInsights = useMemo(() => {
    const converted = closedTradesForPositionSize
      .map((t) =>
        convertPositionToDisplay(
          t.total_entry_amount!,
          t.entry_date!,
          t.currency ?? null
        )
      )
      .filter((a): a is number => a != null && a > 0)
    return calcPositionSizeDisciplineInsights(converted)
  }, [closedTradesForPositionSize, convertPositionToDisplay])

  const dataQualityMessages = useMemo(() => {
    const { dataQuality } = performanceInsights
    const messages: string[] = []
    if (dataQuality.totalClosedTrades === 0) {
      return [t('insights:dataQuality.noData')]
    }
    if (dataQuality.strategyCoveragePct < DATA_QUALITY_THRESHOLD) {
      messages.push(t('insights:dataQuality.noStrategy'))
    }
    if (dataQuality.timeframeCoveragePct < DATA_QUALITY_THRESHOLD) {
      messages.push(t('insights:dataQuality.noTimeframe'))
    }
    if (dataQuality.exchangeCoveragePct < DATA_QUALITY_THRESHOLD) {
      messages.push(t('insights:dataQuality.noExchange'))
    }
    return messages
  }, [performanceInsights.dataQuality, t])

  const hasInsight = useMemo(() => {
    const { bestStrategy, bestMarket, bestTimeframe, bestExchange } = performanceInsights
    return bestStrategy || bestMarket || bestTimeframe || bestExchange
  }, [performanceInsights])

  const hasInsufficientData = useMemo(() => {
    return (
      performanceInsights.dataQuality.totalClosedTrades > 0 &&
      !hasInsight &&
      dataQualityMessages.length === 0
    )
  }, [performanceInsights.dataQuality.totalClosedTrades, hasInsight, dataQualityMessages.length])

  const topRecommendations = useMemo(() => {
    const recs: string[] = []
    if (mistakeDetectionInsights.tradesWithoutStopLoss > 0 || riskManagementInsights.stopLossUsageRate < 100) {
      recs.push('insights:summary.rec1')
    }
    if (
      executionInsights.averagePlannedRR != null &&
      executionInsights.averageRealizedRR != null &&
      executionInsights.averageRealizedRR < executionInsights.averagePlannedRR - 0.3
    ) {
      recs.push('insights:summary.rec2')
    }
    if (mistakeDetectionInsights.belowPlannedRRPct != null && mistakeDetectionInsights.belowPlannedRRPct > 0) {
      recs.push('insights:summary.rec8')
    }
    if (riskManagementInsights.averageRiskPercent != null && riskManagementInsights.averageRiskPercent > 2) {
      recs.push('insights:summary.rec3')
    }
    if (riskManagementInsights.averageRR != null && riskManagementInsights.averageRR < 1) {
      recs.push('insights:summary.rec4')
    }
    if (positionSizeInsights.hasHighVariance) {
      recs.push('insights:summary.rec5')
    }
    if (performanceInsights.dataQuality.strategyCoveragePct < DATA_QUALITY_THRESHOLD && performanceInsights.dataQuality.totalClosedTrades > 0) {
      recs.push('insights:summary.rec6')
    }
    if (performanceInsights.dataQuality.timeframeCoveragePct < DATA_QUALITY_THRESHOLD && performanceInsights.dataQuality.totalClosedTrades > 0) {
      recs.push('insights:summary.rec7')
    }
    return recs.slice(0, 3)
  }, [
    mistakeDetectionInsights,
    riskManagementInsights,
    executionInsights,
    positionSizeInsights,
    performanceInsights.dataQuality,
  ])

  if (loading) {
    return (
      <div className="w-full px-4 sm:px-6 py-6 flex items-center justify-center min-h-[200px]">
        <p className="text-gray-500">{t('header.loading', { ns: 'common' })}</p>
      </div>
    )
  }

  return (
    <div className="dashboard-page-content">
      <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
        {t('nav.insights', { ns: 'common' })}
      </h1>

      {/* AI Summary */}
      <section className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h2 className="text-base md:text-lg font-semibold text-gray-900">
            {t('insights:aiSummary.title')}
          </h2>
          {isAdmin && (
            <button
              type="button"
              onClick={generateAiInsight}
              disabled={aiInsightGenerating}
              className="px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {aiInsightGenerating
                ? t('insights:aiSummary.generating')
                : t('insights:aiSummary.generateNow')}
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {t('insights:aiSummary.subtitle', { year: new Date().getFullYear() })}
        </p>
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 shadow-sm border border-violet-100 rounded-2xl p-4 md:p-6">
          {!canUseAiInsights ? (
            <p className="text-sm text-violet-700">
              {t('insights:aiSummary.membersOnly')}
            </p>
          ) : aiInsightLoading ? (
            <p className="text-sm text-gray-500">
              {t('header.loading', { ns: 'common' })}
            </p>
          ) : aiInsight ? (
            <>
              <p className="text-sm text-gray-800 whitespace-pre-line leading-6">
                {aiInsight.summary}
              </p>
              <p className="mt-4 pt-3 border-t border-violet-100 text-xs text-gray-500">
                {t('insights:aiSummary.generatedAt', {
                  date: new Date(aiInsight.generated_at).toLocaleDateString(),
                  count: aiInsight.trade_count,
                })}
              </p>
            </>
          ) : (
            <p className="text-sm text-violet-700">
              {t('insights:aiSummary.pending')}
            </p>
          )}
          {aiGenerationStatus && (
            <p
              className={`mt-3 text-xs ${
                aiGenerationStatus === 'success' ? 'text-green-700' : 'text-red-600'
              }`}
            >
              {t(`insights:aiSummary.${
                aiGenerationStatus === 'success'
                  ? 'generateSuccess'
                  : aiGenerationStatus === 'no-data'
                    ? 'noTrades'
                    : 'generateError'
              }`)}
            </p>
          )}
        </div>
      </section>

      {/* Time range selector */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedRange(option.value)}
              className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg border transition-colors ${
                selectedRange === option.value
                  ? 'bg-orange-500 border-orange-500 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-500'
              }`}
            >
              {t(option.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <section className="mb-8">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
          {t('insights:summary.title')}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('insights:summary.subtitle')}
        </p>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 shadow-sm border border-orange-100 rounded-2xl p-4 md:p-6">
          {performanceInsights.dataQuality.totalClosedTrades === 0 ? (
            <p className="text-sm text-amber-600">
              {t('insights:summary.noData')}
            </p>
          ) : topRecommendations.length === 0 ? (
            <p className="text-sm text-green-700">
              {t('insights:mistake.noMistakes')}
            </p>
          ) : (
            <ol className="space-y-3">
              {topRecommendations.map((key, i) => (
                <li key={key} className="flex gap-3 text-sm text-gray-800">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Performance Insights */}
      <section className="mb-8">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
          {t('insights:performanceInsights')}
        </h2>
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {performanceInsights.dataQuality.totalClosedTrades === 0 ? (
            <p className="text-sm text-amber-600">
              {t('insights:dataQuality.noData')}
            </p>
          ) : hasInsufficientData ? (
            <p className="text-sm text-amber-600">
              {t('insights:dataQuality.insufficientData')}
            </p>
          ) : (
            <>
              {dataQualityMessages.length > 0 && (
                <div className="mb-4 space-y-2">
                  {dataQualityMessages.map((msg, i) => (
                    <p key={i} className="text-sm text-amber-600">
                      {msg}
                    </p>
                  ))}
                </div>
              )}
              {hasInsight && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    {t('insights:performanceTable.description')}
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.category')}
                        </th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.best')}
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.trades')}
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.winRate')}
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.avgPnlPct')}
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.profitFactor')}
                        </th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-700">
                          {t('insights:performanceTable.totalPnl')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceInsights.dataQuality.strategyCoveragePct >= DATA_QUALITY_THRESHOLD &&
                        performanceInsights.bestStrategy && (
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-3 text-gray-600">
                              {t('insights:performanceTable.strategy')}
                            </td>
                            <td className="py-2 px-3 font-medium text-gray-900">
                              {performanceInsights.bestStrategy.displayName}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestStrategy.totalTrades}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestStrategy.winRate.toFixed(0)}%
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestStrategy.avgPnlPct != null
                                ? `${performanceInsights.bestStrategy.avgPnlPct >= 0 ? '+' : ''}${performanceInsights.bestStrategy.avgPnlPct.toFixed(1)}%`
                                : '–'}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestStrategy.profitFactor === Infinity
                                ? '∞'
                                : performanceInsights.bestStrategy.profitFactor.toFixed(1)}
                            </td>
                            <td
                              className={`py-2 px-3 text-right font-medium ${
                                performanceInsights.bestStrategy.totalPnl >= 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {performanceInsights.bestStrategy.totalPnl >= 0 ? '+' : ''}
                              {performanceInsights.bestStrategy.totalPnl.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{' '}
                              {selectedCurrency}
                            </td>
                          </tr>
                        )}
                      {performanceInsights.dataQuality.timeframeCoveragePct >= DATA_QUALITY_THRESHOLD &&
                        performanceInsights.bestTimeframe &&
                        performanceInsights.bestTimeframe.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE && (
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-3 text-gray-600">
                              {t('insights:performanceTable.timeframe')}
                            </td>
                            <td className="py-2 px-3 font-medium text-gray-900">
                              {performanceInsights.bestTimeframe.displayName}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestTimeframe.totalTrades}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestTimeframe.winRate.toFixed(0)}%
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestTimeframe.avgPnlPct != null
                                ? `${performanceInsights.bestTimeframe.avgPnlPct >= 0 ? '+' : ''}${performanceInsights.bestTimeframe.avgPnlPct.toFixed(1)}%`
                                : '–'}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestTimeframe.profitFactor === Infinity
                                ? '∞'
                                : performanceInsights.bestTimeframe.profitFactor.toFixed(1)}
                            </td>
                            <td
                              className={`py-2 px-3 text-right font-medium ${
                                performanceInsights.bestTimeframe.totalPnl >= 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {performanceInsights.bestTimeframe.totalPnl >= 0 ? '+' : ''}
                              {performanceInsights.bestTimeframe.totalPnl.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{' '}
                              {selectedCurrency}
                            </td>
                          </tr>
                        )}
                      {performanceInsights.bestMarket &&
                        performanceInsights.bestMarket.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE && (
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-3 text-gray-600">
                            {t('insights:performanceTable.market')}
                          </td>
                          <td className="py-2 px-3 font-medium text-gray-900">
                            {performanceInsights.bestMarket.displayName}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {performanceInsights.bestMarket.totalTrades}
                          </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestMarket.winRate.toFixed(0)}%
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestMarket.avgPnlPct != null
                                ? `${performanceInsights.bestMarket.avgPnlPct >= 0 ? '+' : ''}${performanceInsights.bestMarket.avgPnlPct.toFixed(1)}%`
                                : '–'}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestMarket.profitFactor === Infinity
                                ? '∞'
                                : performanceInsights.bestMarket.profitFactor.toFixed(1)}
                            </td>
                            <td
                              className={`py-2 px-3 text-right font-medium ${
                                performanceInsights.bestMarket.totalPnl >= 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {performanceInsights.bestMarket.totalPnl >= 0 ? '+' : ''}
                              {performanceInsights.bestMarket.totalPnl.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{' '}
                              {selectedCurrency}
                            </td>
                          </tr>
                        )}
                      {performanceInsights.dataQuality.exchangeCoveragePct >= DATA_QUALITY_THRESHOLD &&
                        performanceInsights.bestExchange &&
                        performanceInsights.bestExchange.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE && (
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-3 text-gray-600">
                              {t('insights:performanceTable.exchange')}
                            </td>
                            <td className="py-2 px-3 font-medium text-gray-900">
                              {performanceInsights.bestExchange.displayName}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestExchange.totalTrades}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestExchange.winRate.toFixed(0)}%
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestExchange.avgPnlPct != null
                                ? `${performanceInsights.bestExchange.avgPnlPct >= 0 ? '+' : ''}${performanceInsights.bestExchange.avgPnlPct.toFixed(1)}%`
                                : '–'}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {performanceInsights.bestExchange.profitFactor === Infinity
                                ? '∞'
                                : performanceInsights.bestExchange.profitFactor.toFixed(1)}
                            </td>
                            <td
                              className={`py-2 px-3 text-right font-medium ${
                                performanceInsights.bestExchange.totalPnl >= 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {performanceInsights.bestExchange.totalPnl >= 0 ? '+' : ''}
                              {performanceInsights.bestExchange.totalPnl.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{' '}
                              {selectedCurrency}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                  {(() => {
                    const hasEnoughStrategy =
                      performanceInsights.bestStrategy &&
                      performanceInsights.bestStrategy.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE
                    const hasEnoughTimeframe =
                      performanceInsights.bestTimeframe &&
                      performanceInsights.bestTimeframe.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE
                    const hasEnoughMarket =
                      performanceInsights.bestMarket &&
                      performanceInsights.bestMarket.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE
                    const hasEnoughExchange =
                      performanceInsights.bestExchange &&
                      performanceInsights.bestExchange.totalTrades >= MIN_TRADES_FOR_PERFORMANCE_TABLE
                    const hasAnyRow =
                      hasEnoughStrategy ||
                      hasEnoughTimeframe ||
                      hasEnoughMarket ||
                      hasEnoughExchange
                    return !hasAnyRow ? (
                      <p className="text-sm text-amber-600 py-4">
                        {t('insights:performanceTable.minTrades', {
                          min: MIN_TRADES_FOR_PERFORMANCE_TABLE,
                        })}
                      </p>
                    ) : null
                  })()}
                </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Execution Insights */}
      <section className="mb-8">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
          {t('insights:executionInsights')}
        </h2>
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {executionInsights.totalTradesWithBoth === 0 ? (
            <p className="text-sm text-amber-600">
              {t('insights:execution.noData')}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  {t('insights:execution.avgPlannedRR')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {executionInsights.averagePlannedRR != null
                      ? executionInsights.averagePlannedRR.toFixed(2)
                      : '–'}
                  </span>
                </p>
                <p>
                  {t('insights:execution.avgRealizedRR')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {executionInsights.averageRealizedRR != null
                      ? executionInsights.averageRealizedRR.toFixed(2)
                      : '–'}
                  </span>
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                {executionInsights.averagePlannedRR != null &&
                  executionInsights.averageRealizedRR != null && (
                    <p
                      className={`text-sm ${
                        executionInsights.averageRealizedRR <
                        executionInsights.averagePlannedRR - 0.3
                          ? 'text-amber-600'
                          : executionInsights.averageRealizedRR >=
                              executionInsights.averagePlannedRR
                            ? 'text-green-700'
                            : 'text-gray-700'
                      }`}
                    >
                      {executionInsights.averageRealizedRR <
                      executionInsights.averagePlannedRR - 0.3
                        ? t('insights:execution.closeWinnersEarly')
                        : executionInsights.averageRealizedRR >=
                            executionInsights.averagePlannedRR + 0.2
                          ? t('insights:execution.letWinnersRun')
                          : t('insights:execution.executionMatchesPlan')}
                    </p>
                  )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mistake Detection */}
      <section className="mb-8">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
          {t('insights:mistakeDetection')}
        </h2>
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {mistakeDetectionInsights.totalTrades === 0 ? (
            <p className="text-sm text-amber-600">
              {t('insights:dataQuality.noData')}
            </p>
          ) : (
            <div className="space-y-4">
              {mistakeDetectionInsights.tradesWithoutStopLoss > 0 && (
                <p className="text-sm text-amber-600">
                  {t('insights:mistake.noStopLoss', {
                    pct: mistakeDetectionInsights.noStopLossPct.toFixed(0),
                  })}
                </p>
              )}
              {mistakeDetectionInsights.belowPlannedRRPct != null &&
                mistakeDetectionInsights.belowPlannedRRPct > 0 && (
                  <p className="text-sm text-amber-600">
                    {t('insights:mistake.belowPlannedRR', {
                      pct: mistakeDetectionInsights.belowPlannedRRPct.toFixed(0),
                    })}
                  </p>
                )}
              {mistakeDetectionInsights.tradesWithoutStopLoss === 0 &&
                (mistakeDetectionInsights.belowPlannedRRPct == null ||
                  mistakeDetectionInsights.belowPlannedRRPct === 0) && (
                  <p className="text-sm text-green-700">
                    {t('insights:mistake.noMistakes')}
                  </p>
                )}
            </div>
          )}
        </div>
      </section>

      {/* Position Size Discipline */}
      <section className="mb-8">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
          {t('insights:positionSizeDiscipline')}
        </h2>
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {positionSizeInsights.totalTrades < 2 ? (
            <p className="text-sm text-amber-600">
              {t('insights:positionSize.noData')}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  {t('insights:positionSize.avgPositionSize')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {positionSizeInsights.averagePositionSize != null
                      ? positionSizeInsights.averagePositionSize.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : '–'}{' '}
                    {selectedCurrency}
                  </span>
                </p>
                <p>
                  {t('insights:positionSize.coefficientOfVariation')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {positionSizeInsights.coefficientOfVariation != null
                      ? `${(positionSizeInsights.coefficientOfVariation * 100).toFixed(0)}%`
                      : '–'}
                  </span>
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200">
                {positionSizeInsights.hasHighVariance ? (
                  <p className="text-sm text-amber-600">
                    {t('insights:positionSize.highVariance')}
                  </p>
                ) : (
                  <p className="text-sm text-green-700">
                    {t('insights:positionSize.consistent')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Risk Management Insights */}
      <section>
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
          {t('insights:riskManagementInsights')}
        </h2>
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          {riskManagementInsights.totalTrades === 0 ? (
            <p className="text-sm text-amber-600">
              {t('insights:dataQuality.noData')}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  {t('insights:riskManagement.avgRisk')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {riskManagementInsights.averageRiskPercent != null
                      ? `${riskManagementInsights.averageRiskPercent.toFixed(1)}%`
                      : '–'}
                  </span>
                </p>
                <p>
                  {t('insights:riskManagement.stopLossUsage')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {riskManagementInsights.stopLossUsageRate.toFixed(0)}%
                  </span>
                </p>
                <p>
                  {t('insights:riskManagement.avgRR')}:{' '}
                  <span className="font-semibold text-gray-900">
                    {riskManagementInsights.averageRR != null
                      ? riskManagementInsights.averageRR.toFixed(2)
                      : '–'}
                  </span>
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200 space-y-2">
                {riskManagementInsights.averageRiskPercent != null &&
                  riskManagementInsights.averageRiskPercent > 2 && (
                    <p className="text-sm text-gray-800">
                      {t('insights:riskManagement.riskTooHigh', {
                        avg: riskManagementInsights.averageRiskPercent.toFixed(1),
                      })}
                    </p>
                  )}
                {riskManagementInsights.averageRiskPercent != null &&
                  riskManagementInsights.averageRiskPercent <= 2 && (
                  <p className="text-sm text-green-700">
                    {t('insights:riskManagement.riskGood', {
                      avg: riskManagementInsights.averageRiskPercent.toFixed(1),
                    })}
                  </p>
                )}
                {riskManagementInsights.stopLossUsageRate < 100 && (
                  <p className="text-sm text-amber-600">
                    {t('insights:riskManagement.slUsageLow', {
                      pct: riskManagementInsights.stopLossUsageRate.toFixed(0),
                    })}
                  </p>
                )}
                {riskManagementInsights.averageRR != null &&
                  riskManagementInsights.averageRR < 1 && (
                    <p className="text-sm text-amber-600">
                      {t('insights:riskManagement.rrLow', {
                        rr: riskManagementInsights.averageRR.toFixed(2),
                      })}
                    </p>
                  )}
                {riskManagementInsights.averageRR != null &&
                  riskManagementInsights.averageRR >= 1.5 && (
                    <p className="text-sm text-green-700">
                      {t('insights:riskManagement.rrGood', {
                        rr: riskManagementInsights.averageRR.toFixed(2),
                      })}
                    </p>
                  )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Insights
